import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DocumentEditAndReadService } from '@secca/core/services/document-edit-and-read.service';
import {
  DocumentEditAndReadDocument,
  DocumentEdit,
  DocumentEditJob,
  DocumentPIIEntity,
  DocumentPIIEntityInput,
  SubmitDocumentTranslationRequest
} from '@secca/shared/models/atlas/atlas-document-edit-and-read.model';
import { DialogHelperUtilService } from '@secca/core/services/dialog-helper-util.service';
import { DialogBoundryService } from '@secca/core/services/dialog-boundry.service';
import {
  DocumentPreviewDialogComponent,
  DocumentPreviewDialogData
} from '@secca/shared/components/atlas-chatbot/document-preview-dialog/document-preview-dialog.component';
import {
  DocumentEditTextDialogComponent,
  DocumentEditTextDialogData
} from '@secca/shared/components/atlas-chatbot/document-edit-text-dialog/document-edit-text-dialog.component';
import { escapeRegExp } from '@secca/shared/helpers/string-helper';
import { forkJoin } from 'rxjs';

export interface DocumentTranslationsDialogData {
  caseNumber: string;
  caseLabel: string;
  suggestedPiiEntities?: DocumentPIIEntityInput[];
}

interface TranslationLanguageOption {
  code: 'DK' | 'GB' | 'SE' | 'NO';
  label: string;
  value: 'DANISH' | 'ENGLISH' | 'SWEDISH' | 'NORWEGIAN';
}

interface PiiEntityTypeOption {
  label: string;
  value: 'ADDRESS' | 'FULL_NAME' | 'EMAIL' | 'NATIONAL_ID' | 'PHONE_NUMBER' | 'OTHER';
}

interface SuggestedPiiEntity extends DocumentPIIEntityInput {
  label: string;
}

type DetailTab = 'pii' | 'translate';

@Component({
  selector: 'app-document-translations-dialog',
  templateUrl: './document-translations-dialog.component.html',
  styleUrls: ['./document-translations-dialog.component.scss'],
  standalone: false
})
export class DocumentTranslationsDialogComponent implements OnInit {
  readonly translationLanguages: TranslationLanguageOption[] = [
    { code: 'DK', label: 'Danish', value: 'DANISH' },
    { code: 'GB', label: 'English', value: 'ENGLISH' },
    { code: 'SE', label: 'Swedish', value: 'SWEDISH' },
    { code: 'NO', label: 'Norwegian', value: 'NORWEGIAN' }
  ];
  readonly piiEntityTypeOptions: PiiEntityTypeOption[] = [
    { label: 'Address', value: 'ADDRESS' },
    { label: 'Full name', value: 'FULL_NAME' },
    { label: 'Email', value: 'EMAIL' },
    { label: 'National ID', value: 'NATIONAL_ID' },
    { label: 'Phone number', value: 'PHONE_NUMBER' },
    { label: 'Other', value: 'OTHER' }
  ];
  readonly suggestedPiiEntities: SuggestedPiiEntity[];

  documents: DocumentEditAndReadDocument[] = [];
  selectedDocument: DocumentEditAndReadDocument | null = null;
  selectedDocumentEditJobs: DocumentEditJob[] = [];
  activeDetailTab: DetailTab = 'pii';
  selectedLanguage: 'DANISH' | 'ENGLISH' | 'SWEDISH' | 'NORWEGIAN' = 'ENGLISH';
  isLoading = true;
  isLoadingEditJobs = false;
  isSubmittingTranslation = false;
  isSavingPiiEntities = false;
  downloadingJobId: number | null = null;
  openingTextJobId: number | null = null;
  errorMessage = '';
  editJobsErrorMessage = '';
  submitErrorMessage = '';
  submitSuccessMessage = '';
  piiEntityType: PiiEntityTypeOption['value'] | null = null;
  piiCustomEntityType = '';
  piiEntityValue = '';
  piiErrorMessage = '';
  piiSuccessMessage = '';

  constructor(
    private dialogRef: MatDialogRef<DocumentTranslationsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentTranslationsDialogData,
    private documentEditAndReadService: DocumentEditAndReadService,
    private dialogHelperUtilService: DialogHelperUtilService,
    public dialogBoundryService: DialogBoundryService
  ) {
    this.suggestedPiiEntities = (data.suggestedPiiEntities ?? []).map(entity => ({
      ...entity,
      label: this.getSuggestedPiiLabel(entity.entity_type)
    }));
  }

  ngOnInit(): void {
    this.loadDocuments();
  }

  close(): void {
    this.dialogRef.close();
  }

  reload(): void {
    this.loadDocuments();
  }

  trackByUuid(_index: number, document: DocumentEditAndReadDocument): string {
    return document.uuid;
  }

  getTranslationLanguage(job: DocumentEditJob): string {
    const language = job.additional_input_job_list?.find(input => input.key === 'TRANSLATE_TO')?.value;
    return language ? language.charAt(0) + language.slice(1).toLowerCase() : '-';
  }

  getTranslationLanguageFlag(job: DocumentEditJob): string | null {
    const language = job.additional_input_job_list?.find(input => input.key === 'TRANSLATE_TO')?.value;
    switch (language) {
      case 'DANISH':
        return 'dk';
      case 'ENGLISH':
        return 'gb';
      case 'SWEDISH':
        return 'se';
      case 'NORWEGIAN':
        return 'no';
      default:
        return null;
    }
  }

  getStatusBadgeClass(status?: string): string {
    switch (status) {
      case 'DONE':
        return 'status-done';
      case 'FAILED':
      case 'DECLINED':
        return 'status-failed';
      default:
        return 'status-progress';
    }
  }

  getStatusIcon(status?: string): string {
    switch (status) {
      case 'DONE':
        return '✓';
      case 'FAILED':
      case 'DECLINED':
        return '!';
      default:
        return '…';
    }
  }

  getTranslationDuration(job: DocumentEditJob): string | null {
    if (!job.created_at || !job.done_at) {
      return null;
    }

    const createdAt = new Date(job.created_at).getTime();
    const doneAt = new Date(job.done_at).getTime();

    if (Number.isNaN(createdAt) || Number.isNaN(doneAt) || doneAt < createdAt) {
      return null;
    }

    return `${Math.round((doneAt - createdAt) / 1000)} sec`;
  }

  selectDocument(document: DocumentEditAndReadDocument): void {
    this.selectedDocument = document;
    this.activeDetailTab = 'pii';
    this.selectedDocumentEditJobs = [];
    this.editJobsErrorMessage = '';
    this.submitErrorMessage = '';
    this.submitSuccessMessage = '';
    this.piiEntityType = null;
    this.piiCustomEntityType = '';
    this.piiEntityValue = '';
    this.piiErrorMessage = '';
    this.piiSuccessMessage = '';
    this.isSavingPiiEntities = false;
    this.downloadingJobId = null;
    this.openingTextJobId = null;

    if (document.id == null) {
      this.editJobsErrorMessage = 'This document does not have an ID that can be used to load edit jobs.';
      return;
    }

    this.loadSelectedDocumentEditJobs(document.id);
  }

  refreshSelectedDocumentEditJobs(): void {
    if (!this.selectedDocument?.id) {
      return;
    }

    this.loadSelectedDocumentEditJobs(this.selectedDocument.id);
  }

  setActiveDetailTab(tab: DetailTab): void {
    this.activeDetailTab = tab;
  }

  addPiiEntity(): void {
    if (!this.selectedDocument?.id || this.isSavingPiiEntities) {
      return;
    }

    const entityType = this.resolvePiiEntityType();
    const value = this.piiEntityValue.trim();

    if (!entityType || !value) {
      this.piiErrorMessage = 'Enter both a PII type and a value.';
      this.piiSuccessMessage = '';
      return;
    }

    const existingEntities = this.selectedDocument.pii_entities ?? [];
    const payload: DocumentPIIEntityInput[] = [
      ...existingEntities.map(entity => ({ entity_type: entity.entity_type, value: entity.value })),
      { entity_type: this.getNextPiiEntityType(entityType, existingEntities), value }
    ];

    this.persistPiiEntities(payload, 'PII entity added.', () => {
      this.piiEntityType = null;
      this.piiCustomEntityType = '';
      this.piiEntityValue = '';
    });
  }

  addSuggestedPiiEntity(entity: SuggestedPiiEntity): void {
    if (!this.selectedDocument?.id || this.isSavingPiiEntities || this.hasPiiEntity(entity)) {
      return;
    }

    const existingEntities = this.selectedDocument.pii_entities ?? [];
    const payload: DocumentPIIEntityInput[] = [
      ...existingEntities.map(existingEntity => ({
        entity_type: existingEntity.entity_type,
        value: existingEntity.value
      })),
      {
        entity_type: this.getNextPiiEntityType(entity.entity_type, existingEntities),
        value: entity.value
      }
    ];

    this.persistPiiEntities(payload, `${entity.label} added.`);
  }

  removePiiEntity(entityToRemove: DocumentPIIEntity): void {
    if (!this.selectedDocument?.id || this.isSavingPiiEntities) {
      return;
    }

    const remainingEntities = (this.selectedDocument.pii_entities ?? []).filter(entity => entity !== entityToRemove);
    if (remainingEntities.length === 0) {
      this.isSavingPiiEntities = true;
      this.piiErrorMessage = '';
      this.piiSuccessMessage = '';
      this.documentEditAndReadService.deleteDocumentPiiEntities(this.selectedDocument.id).subscribe(
        () => {
          this.updateSelectedDocumentPiiEntities([]);
          this.isSavingPiiEntities = false;
          this.piiSuccessMessage = 'PII entities cleared.';
        },
        () => {
          this.isSavingPiiEntities = false;
          this.piiErrorMessage = 'Unable to remove the PII entity.';
        }
      );
      return;
    }

    this.persistPiiEntities(
      remainingEntities.map(entity => ({ entity_type: entity.entity_type, value: entity.value })),
      'PII entity removed.'
    );
  }

  downloadTranslatedDocument(job: DocumentEditJob): void {
    if (!this.selectedDocument?.id || !job.id || this.downloadingJobId != null) {
      return;
    }

    this.downloadingJobId = job.id;
    this.submitErrorMessage = '';
    this.documentEditAndReadService.downloadDocumentEdit(this.selectedDocument.id, job.id, 'PDF').subscribe(
      base64Document => {
        this.openPdfPreviewDialog(base64Document, job);
        this.downloadingJobId = null;
      },
      () => {
        this.submitErrorMessage = 'Unable to download the translated document.';
        this.downloadingJobId = null;
      }
    );
  }

  openTranslatedText(job: DocumentEditJob): void {
    if (!this.selectedDocument?.id || !job.id || this.openingTextJobId != null) {
      return;
    }

    this.openingTextJobId = job.id;
    this.submitErrorMessage = '';
    forkJoin({
      originalDocument: this.documentEditAndReadService.getDocument(this.selectedDocument.id, true),
      documentEdit: this.documentEditAndReadService.getDocumentEdit(this.selectedDocument.id, job.id)
    }).subscribe(
      ({ originalDocument, documentEdit }) => {
        this.openDocumentEditTextDialog(originalDocument, documentEdit, job);
        this.openingTextJobId = null;
      },
      () => {
        this.submitErrorMessage = 'Unable to load the translated text.';
        this.openingTextJobId = null;
      }
    );
  }

  private loadSelectedDocumentEditJobs(documentId: number): void {
    this.isLoadingEditJobs = true;
    this.editJobsErrorMessage = '';
    this.documentEditAndReadService.getDocumentEditJobs(documentId).subscribe(
      jobs => {
        this.selectedDocumentEditJobs = [...jobs].sort((a, b) => {
          const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
          const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
          if (dateA !== dateB) {
            return dateB - dateA;
          }
          return (b.id ?? 0) - (a.id ?? 0);
        });
        this.isLoadingEditJobs = false;
      },
      () => {
        this.selectedDocumentEditJobs = [];
        this.editJobsErrorMessage = 'Unable to load edit jobs for this document.';
        this.isLoadingEditJobs = false;
      }
    );
  }

  submitTranslation(): void {
    if (!this.selectedDocument || this.selectedDocument.id == null || this.isSubmittingTranslation) {
      return;
    }

    this.isSubmittingTranslation = true;
    this.submitErrorMessage = '';
    this.submitSuccessMessage = '';

    const payload: SubmitDocumentTranslationRequest = {
      edit_method: 'TRANSLATE',
      extension_to_edit_from: this.selectedDocument.extension || 'pdf',
      additional_job_input_list: [
        {
          key: 'TRANSLATE_TO',
          value: this.selectedLanguage
        }
      ]
    };

    this.documentEditAndReadService.createDocumentEditJob(this.selectedDocument.id, payload).subscribe(
      createdJob => {
        this.isSubmittingTranslation = false;
        this.submitSuccessMessage = `Translation submitted for ${this.selectedLanguage.toLowerCase()}.`;
        this.selectedDocumentEditJobs = createdJob ? [createdJob, ...this.selectedDocumentEditJobs] : this.selectedDocumentEditJobs;
      },
      () => {
        this.isSubmittingTranslation = false;
        this.submitErrorMessage = 'Unable to submit the translation request for this document.';
      }
    );
  }

  trackByPiiEntity(index: number, entity: DocumentPIIEntity): string {
    return entity.id != null ? `${entity.id}` : `${entity.entity_type}-${entity.value}-${index}`;
  }

  getVisibleSuggestedPiiEntities(): SuggestedPiiEntity[] {
    return this.suggestedPiiEntities.filter(entity => !this.hasPiiEntity(entity));
  }

  hasPiiEntity(entity: DocumentPIIEntityInput): boolean {
    const entityTypeMatcher = new RegExp(`^${escapeRegExp(entity.entity_type)}(\\d+)?$`);
    return (this.selectedDocument?.pii_entities ?? []).some(existingEntity =>
      entityTypeMatcher.test(existingEntity.entity_type || '') && existingEntity.value === entity.value
    );
  }

  private loadDocuments(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.selectedDocument = null;
    this.selectedDocumentEditJobs = [];
    this.editJobsErrorMessage = '';
    this.isLoadingEditJobs = false;
    this.isSubmittingTranslation = false;
    this.downloadingJobId = null;
    this.openingTextJobId = null;
    this.submitErrorMessage = '';
    this.submitSuccessMessage = '';
    this.piiEntityType = null;
    this.piiCustomEntityType = '';
    this.piiEntityValue = '';
    this.piiErrorMessage = '';
    this.piiSuccessMessage = '';

    this.documentEditAndReadService.getDocumentsForCase(this.data.caseNumber).subscribe(
      documents => {
        this.documents = documents;
        this.isLoading = false;
      },
      () => {
        this.documents = [];
        this.errorMessage = 'Unable to load translated documents for this case.';
        this.isLoading = false;
      }
    );
  }

  private openPdfPreviewDialog(base64Document: string, job: DocumentEditJob): void {
    const previewData: DocumentPreviewDialogData = {
      base64ForDocument: base64Document,
      extension: 'pdf',
      title: `Translated Preview · Job ${job.id ?? '-'}`
    };

    this.dialogHelperUtilService.open(DocumentPreviewDialogComponent, {
      id: job.id ? `TranslatedDocument-${job.id}-Preview` : undefined,
      width: '1150px',
      data: previewData
    });
  }

  private openDocumentEditTextDialog(
    originalDocument: DocumentEditAndReadDocument,
    documentEdit: DocumentEdit,
    job: DocumentEditJob
  ): void {
    const dialogData: DocumentEditTextDialogData = {
      title: `Text View · Translation ${job.id ?? '-'}`,
      originalDocument,
      documentEdit
    };

    this.dialogHelperUtilService.open(DocumentEditTextDialogComponent, {
      id: job.id ? `DocumentEditTextDialog-${job.id}` : undefined,
      width: '980px',
      data: dialogData
    });
  }

  private persistPiiEntities(
    entities: DocumentPIIEntityInput[],
    successMessage: string,
    onSuccess?: () => void
  ): void {
    if (!this.selectedDocument?.id) {
      return;
    }

    this.isSavingPiiEntities = true;
    this.piiErrorMessage = '';
    this.piiSuccessMessage = '';

    this.documentEditAndReadService.replaceDocumentPiiEntities(this.selectedDocument.id, entities).subscribe(
      updatedEntities => {
        this.updateSelectedDocumentPiiEntities(updatedEntities);
        this.isSavingPiiEntities = false;
        this.piiSuccessMessage = successMessage;
        if (onSuccess) {
          onSuccess();
        }
      },
      () => {
        this.isSavingPiiEntities = false;
        this.piiErrorMessage = 'Unable to update PII entities for this document.';
      }
    );
  }

  private updateSelectedDocumentPiiEntities(piiEntities: DocumentPIIEntity[]): void {
    if (!this.selectedDocument) {
      return;
    }

    const updatedDocument: DocumentEditAndReadDocument = {
      ...this.selectedDocument,
      pii_entities: piiEntities
    };

    this.selectedDocument = updatedDocument;
    this.documents = this.documents.map(document =>
      document.uuid === updatedDocument.uuid ? updatedDocument : document
    );
  }

  private getSuggestedPiiLabel(entityType: string): string {
    switch (entityType) {
      case 'CASE_NUMBER':
        return 'Case number';
      case 'FULL_NAME':
        return 'Full name';
      case 'EMAIL':
        return 'Email';
      case 'NATIONAL_ID':
        return 'National ID';
      case 'PHONE_NUMBER':
        return 'Phone number';
      case 'ADDRESS':
        return 'Address';
      default:
        return entityType;
    }
  }

  private resolvePiiEntityType(): string {
    if (this.piiEntityType === 'OTHER') {
      return this.piiCustomEntityType.trim();
    }

    return this.piiEntityType ?? '';
  }

  private getNextPiiEntityType(baseType: string, existingEntities: DocumentPIIEntity[]): string {
    const trimmedBaseType = baseType.trim();
    if (!trimmedBaseType) {
      return '';
    }

    const matcher = new RegExp(`^${escapeRegExp(trimmedBaseType)}(\\d+)$`);
    let highestSuffix = 0;

    for (const entity of existingEntities) {
      const match = entity.entity_type?.match(matcher);
      if (!match) {
        continue;
      }

      const suffix = Number(match[1]);
      if (!Number.isNaN(suffix) && suffix > highestSuffix) {
        highestSuffix = suffix;
      }
    }

    return `${trimmedBaseType}${highestSuffix + 1}`;
  }

}
